// CreateGroupModal — "Create group" per the design: name, an allow-join-by-link
// card (a toggle + a note that the link/QR are generated once the group exists —
// no per-group link until then), and a `UsersPicker` member list. The section
// is contextual (passed in), not a field. Submit → POST /groups; cache
// invalidation refetches the list.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Field, Icon, Input, Modal, Switch, UsersPicker } from '@/components/common';
import { useCreateGroupMutation, type GroupSection } from '@/api/endpoints/groupsApi';
import { toastApiError } from '@/utils/apiErrorToast';

interface CreateGroupModalProps {
  open: boolean;
  onClose: () => void;
  sections: GroupSection[];
  /** Section to create into (per-section "Add group"); defaults to Unassigned. */
  defaultSectionId?: string;
}

const CreateGroupModal = ({ open, onClose, sections, defaultSectionId }: CreateGroupModalProps) => {
  const { t } = useTranslation();
  const [createGroup, { isLoading }] = useCreateGroupMutation();

  const [name, setName] = useState('');
  const [allowJoinByLink, setAllowJoinByLink] = useState(false);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);

  // Re-seed on open; discard leftovers on close.
  useEffect(() => {
    if (open) {
      setName('');
      setAllowJoinByLink(false);
      setUserIds([]);
      setSubmitted(false);
    }
  }, [open]);

  // Section is contextual: the passed one, else the default (Unassigned), else first.
  const groupSection =
    defaultSectionId ?? sections.find((s) => s.isDefault)?.id ?? sections[0]?.id ?? '';

  const nameMissing = !name.trim();

  const submit = async () => {
    setSubmitted(true);
    if (nameMissing) return;
    try {
      await createGroup({
        groupName: name.trim(),
        groupSection,
        users: userIds,
        allowJoinByLink,
      }).unwrap();
      toast.success(t('group_created'));
      onClose();
    } catch (err) {
      toastApiError(err, t);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon='users-round'
      title={t('group_create_title')}
      subtitle={t('group_create_subtitle')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={isLoading}>
            {t('button_cancel')}
          </Button>
          <Button iconStart='user-plus' onClick={submit} loading={isLoading}>
            {t('groups_create')}
          </Button>
        </>
      }
    >
      <div className='create-group'>
        <Field
          label={t('group_field_name')}
          required
          error={submitted && nameMissing ? t('group_name_required') : undefined}
        >
          <Input
            value={name}
            placeholder={t('group_field_name_ph')}
            error={submitted && nameMissing}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        {/* ---- Allow join by link ---- */}
        <div className='create-group__join'>
          <div className='create-group__join-head'>
            <span className='create-group__join-tile'>
              <Icon name='link' size={18} />
            </span>
            <span className='create-group__join-text'>
              <span className='create-group__join-title'>{t('group_joinlink_title')}</span>
              <span className='create-group__join-subtitle'>{t('group_joinlink_subtitle')}</span>
            </span>
            <Switch
              checked={allowJoinByLink}
              onChange={(e) => setAllowJoinByLink(e.target.checked)}
              aria-label={t('group_joinlink_title')}
            />
          </div>

          {allowJoinByLink && (
            <p className='create-group__join-note'>
              <Icon name='info' size={16} />
              {t('group_joinlink_pending')}
            </p>
          )}
        </div>

        {/* ---- Select users ---- */}
        <div className='create-group__members'>
          <div className='create-group__members-head'>
            <span className='create-group__members-label'>{t('group_members_select')}</span>
            <span className='create-group__selected'>
              {t('group_members_selected', { count: userIds.length })}
            </span>
          </div>
          <UsersPicker value={userIds} onChange={setUserIds} skip={!open} />
        </div>
      </div>
    </Modal>
  );
};

export default CreateGroupModal;
